import { isPlatformBrowser } from "@angular/common";
import { HttpClient } from "@angular/common/http";
import { Inject, Injectable, PLATFORM_ID, Renderer2 } from "@angular/core";
import { AbstractControl } from "@angular/forms";
import { AuthService } from "@core/services/auth.service";
import { BaseService } from "@core/services/base.service";
import { LoadingService } from "@core/services/loading.service";
import { UtilsService } from "@core/services/utils/utils.service";
import { WindowRefService } from "@core/services/window-ref.service";
import { environment } from "@env/environment";
import { BehaviorSubject, Observable, of } from "rxjs";
import { catchError, map } from "rxjs/operators";

declare const CKEDITOR: any;

/**
 * Timeouts used in the CKEditor service
 */
export const CKEDITOR_TIMEOUTS = {
  /** Maximum time to wait for CKEditor to load (ms) */
  LOAD_TIMEOUT: 20000,
  /** Interval for checking CKEditor status (ms) */
  CHECK_INTERVAL: 500
};

/**
 * CKEditor loading states
 */
export enum CKEditorLoadingState {
  UNINITIALIZED = "uninitialized",
  LOADING = "loading",
  READY = "ready",
  ERROR = "error"
}

@Injectable({
  providedIn: "root"
})
export class CKEditorService extends BaseService {
  // Observable to track the loading state
  private _loadingState = new BehaviorSubject<CKEditorLoadingState>(CKEditorLoadingState.UNINITIALIZED);
  private readonly _isBrowser: boolean;
  private _loadPromise: Promise<void> | null = null;
  private _bbCodePluginLoaded = false;

  constructor(
    public readonly loadingService: LoadingService,
    public readonly authService: AuthService,
    public readonly windowRefService: WindowRefService,
    public readonly http: HttpClient,
    public readonly utilsService: UtilsService,
    @Inject(PLATFORM_ID) private platformId: Object
  ) {
    super(loadingService);
    this._isBrowser = isPlatformBrowser(this.platformId);
  }

  /**
   * Get the current loading state as an observable
   */
  public get loadingState$(): Observable<CKEditorLoadingState> {
    return this._loadingState.asObservable();
  }

  /**
   * Get the current loading state value
   */
  public get loadingState(): CKEditorLoadingState {
    return this._loadingState.value;
  }

  /**
   * Check if CKEditor is ready for use
   */
  public get isReady(): boolean {
    return this._loadingState.value === CKEditorLoadingState.READY;
  }

  /**
   * Centralized check for CKEditor readiness with different requirement levels
   * @param requireEditorCreation Whether to check for editor instance creation capabilities
   * @returns true if CKEditor meets the specified readiness requirements
   */
  public isCKEditorReady(requireEditorCreation = false): boolean {
    if (!this._isBrowser) {
      return false;
    }

    const win = this.windowRefService.nativeWindow as any;

    // Basic availability check
    if (typeof win.CKEDITOR === "undefined" || !win.CKEDITOR) {
      return false;
    }

    // For BBCode filter usage, we need htmlParser
    if (!win.CKEDITOR.htmlParser) {
      return false;
    }

    // For editor creation, we need additional internals
    if (requireEditorCreation) {
      return (
        typeof win.CKEDITOR.replace === "function" &&
        (win.CKEDITOR.status === "loaded" ||
          win.CKEDITOR.status === "basic_ready" ||
          win.CKEDITOR.status === "ready") &&
        win.CKEDITOR.editor &&
        win.CKEDITOR.dom &&
        win.CKEDITOR.dom.selection &&
        typeof win.CKEDITOR.dom.selection.setupEditorOptimization !== "undefined"
      );
    }

    return true;
  }

  setupBBCodeFilter(renderer: Renderer2) {
    // Use centralized readiness check
    if (!this.isCKEditorReady()) {
      console.warn("CKEDITOR not available for setupBBCodeFilter");
      return {}; // Return an empty object that won't break things
    }

    const bbcodeFilter = new CKEDITOR.htmlParser.filter();

    bbcodeFilter.addRules({
      elements: {
        p: element => {
          element.replaceWithChildren();
          element.add(new CKEDITOR.htmlParser.text("\n\n"));
        },
        blockquote: element => {
          const quoted = new CKEDITOR.htmlParser.element("div");
          quoted.children = element.children;
          element.children = [quoted];
          const citeText = element.attributes.cite;
          if (citeText) {
            const cite = new CKEDITOR.htmlParser.element("cite");
            cite.add(new CKEDITOR.htmlParser.text(citeText.replace(/^"|"$/g, "")));
            delete element.attributes.cite;
            element.children.unshift(cite);
          }
        },
        span: element => {
          let bbcode;
          if ((bbcode = element.attributes.bbcode)) {
            if (bbcode === "img") {
              element.name = "img";
              element.attributes.src = element.children[0].value;
              element.children = [];
            } else if (bbcode === "email") {
              element.name = "a";
              element.attributes.href = "mailto:" + element.children[0].value;
            }
          } else if (element.attributes.style && element.attributes.style.includes("font-size:")) {
            // Handle percentage-based font size
            const sizeMatch = element.attributes.style.match(/font-size:(\d+)%/);
            if (sizeMatch) {
              const size = parseInt(sizeMatch[1], 10);
              let remSize;

              // Use the same three-tier system
              if (size <= 50) {
                remSize = ".5rem";
              } else if (size <= 75) {
                remSize = ".75rem";
              } else if (size <= 100) {
                remSize = "1rem";
              } else if (size <= 150) {
                remSize = "1.5rem";
              } else {
                remSize = "2rem";
              }

              // Replace the percentage-based style with rem-based
              element.attributes.style = `font-size: ${remSize}`;
            }
          }
        },
        ol: element => {
          if (element.attributes.listType) {
            if (element.attributes.listType !== "decimal") {
              element.attributes.style = "list-style-type:" + element.attributes.listType;
            }
          } else {
            element.name = "ul";
          }
          delete element.attributes.listType;
        },
        a: element => {
          if (!element.attributes.href) {
            element.attributes.href = element.children[0].value;
          }
        },
        smiley: element => {
          element.name = "img";
          const editorConfig = this.options(null);
          const description = element.attributes.desc;
          const image =
            editorConfig.smiley_images[CKEDITOR.tools.indexOf(editorConfig.smiley_descriptions, description)];
          const src = CKEDITOR.tools.htmlEncode(editorConfig.smiley_path + image);

          element.attributes = {
            src,
            "data-cke-saved-src": src,
            class: "smiley",
            title: description,
            alt: description
          };
        },
        img: element => {
          if (!renderer) {
            return;
          }

          const src: string = element.attributes.src;
          const placeholderSrc = "/assets/images/loading.gif";

          if (src && src.indexOf("/ckeditor-files/") > 0) {
            element.attributes.src = placeholderSrc;

            const fileName = "f" + src.split("/").pop().split(".")[0].replace(/-/gi, "");
            element.attributes.class = `loading ${fileName}`;

            this._fetchThumbnail(src, thumbnailSrc => {
              setTimeout(() => {
                const imgElement = this.windowRefService.nativeWindow.document.querySelector(`img.${fileName}`);
                if (imgElement) {
                  renderer.setAttribute(imgElement, "src", thumbnailSrc);
                  renderer.setAttribute(imgElement, "data-src", src);
                  renderer.removeClass(imgElement, "loading");
                  renderer.removeClass(imgElement, `${fileName}`);
                }
              });
            });
          }
        }
      }
    });

    return bbcodeFilter;
  }

  plugins(): string[] {
    return [
      "ajax",
      "autocomplete",
      "autogrow",
      "autolink",
      "basicstyles",
      "bbcode",
      "blockquote",
      "button",
      "clipboard",
      "codesnippet",
      "dialog",
      "dialogui",
      "divarea",
      "editorplaceholder",
      "enterkey",
      "entities",
      "fakeobjects",
      "filebrowser",
      "filetools",
      "floatpanel",
      "floatingspace",
      "font",
      "help",
      "indent",
      "indentlist",
      "lineutils",
      "link",
      "list",
      "magicline",
      "maximize",
      "mentions",
      "menu",
      "notification",
      "notificationaggregator",
      "panel",
      "pastefromgdocs",
      "pastetools",
      "popup",
      "SimpleLink",
      "simpleuploads",
      "smiley",
      "sourcedialog",
      "specialchar",
      "table",
      "textmatch",
      "textwatcher",
      "toolbar",
      "undo",
      "uploadwidget",
      "widget",
      "widgetselection",
      "xml"
    ];
  }

  mentions(): any {
    return [
      {
        feed: `${
          environment.classicBaseUrl
        }/autocomplete_usernames/?q={encodedQuery}&token=${this.authService.getClassicApiToken()}`,
        marker: "@",
        pattern: new RegExp("@[_a-zA-Z0-9À-ž ]{2,}"),
        itemTemplate: `<li data-id="{id}">
              <img class="avatar" width="40" height="40" src="${environment.cdnUrl}{avatar}" />
              <strong class="realname">{realName}</strong><span class="username">({username})</span>
             </li>`,
        outputTemplate: `<a href="${environment.classicBaseUrl}/users/{username}/">@{displayName}</a><span>&nbsp;</span>`
      },
      {
        feed: `${
          environment.classicBaseUrl
        }/autocomplete_images/?q={encodedQuery}&token=${this.authService.getClassicApiToken()}`,
        marker: "#",
        pattern: new RegExp("#[_a-zA-Z0-9À-ž ]{0,}"),
        itemTemplate: `<li data-id="{id}">
              <img class="image" width="40" height="40" src="{thumbnail}" />
              <span class="title">{title}</span>
            </li>`,
        outputTemplate: `<br/><a href="${environment.classicBaseUrl}{url}">
              <img src="{thumbnail}"/>
              <br/>
              {title}
            </a><br/>`
      }
    ];
  }

  smileyDescriptions(): string[] {
    return [
      "angel",
      "angry-1",
      "angry",
      "arrogant",
      "bored",
      "confused",
      "cool-1",
      "cool",
      "crying-1",
      "crying-2",
      "crying",
      "cute",
      "embarrassed",
      "emoji",
      "greed",
      "happy-1",
      "happy-2",
      "happy-3",
      "happy-4",
      "happy-5",
      "happy-6",
      "happy-7",
      "happy",
      "in-love",
      "kiss-1",
      "kiss",
      "laughing-1",
      "laughing-2",
      "laughing",
      "muted",
      "nerd",
      "sad-1",
      "sad-2",
      "sad",
      "scare",
      "serious",
      "shocked",
      "sick",
      "sleepy",
      "smart",
      "surprised-1",
      "surprised-2",
      "surprised-3",
      "surprised-4",
      "surprised",
      "suspicious",
      "tongue",
      "vain",
      "wink-1",
      "wink"
    ];
  }

  smileyImages(): string[] {
    return [
      "angel.png",
      "angry-1.png",
      "angry.png",
      "arrogant.png",
      "bored.png",
      "confused.png",
      "cool-1.png",
      "cool.png",
      "crying-1.png",
      "crying-2.png",
      "crying.png",
      "cute.png",
      "embarrassed.png",
      "emoji.png",
      "greed.png",
      "happy-1.png",
      "happy-2.png",
      "happy-3.png",
      "happy-4.png",
      "happy-5.png",
      "happy-6.png",
      "happy-7.png",
      "happy.png",
      "in-love.png",
      "kiss-1.png",
      "kiss.png",
      "laughing-1.png",
      "laughing-2.png",
      "laughing.png",
      "muted.png",
      "nerd.png",
      "sad-1.png",
      "sad-2.png",
      "sad.png",
      "scare.png",
      "serious.png",
      "shocked.png",
      "sick.png",
      "sleepy.png",
      "smart.png",
      "surprised-1.png",
      "surprised-2.png",
      "surprised-3.png",
      "surprised-4.png",
      "surprised.png",
      "suspicious.png",
      "tongue.png",
      "vain.png",
      "wink-1.png",
      "wink.png"
    ];
  }

  specialChars(): string[] {
    return [
      "!",
      "&quot;",
      "#",
      "$",
      "%",
      "&amp;",
      "'",
      "(",
      ")",
      "*",
      "+",
      "-",
      ".",
      "/",
      "4",
      "5",
      "6",
      "7",
      "8",
      "9",
      ":",
      ";",
      "&lt;",
      "=",
      "&gt;",
      "?",
      "@",
      "[",
      "]",
      "^",
      "_",
      "`",
      "{",
      "|",
      "}",
      "~",
      "&euro;",
      "&lsquo;",
      "&rsquo;",
      "&ldquo;",
      "&rdquo;",
      "&ndash;",
      "&mdash;",
      "&iexcl;",
      "&cent;",
      "&pound;",
      "&curren;",
      "&yen;",
      "&brvbar;",
      "&sect;",
      "&uml;",
      "&copy;",
      "&ordf;",
      "&laquo;",
      "&not;",
      "&reg;",
      "&macr;",
      "&deg;",
      "&sup2;",
      "&sup3;",
      "&acute;",
      "&micro;",
      "&para;",
      "&middot;",
      "&cedil;",
      "&sup1;",
      "&ordm;",
      "&raquo;",
      "&frac14;",
      "&frac12;",
      "&frac34;",
      "&iquest;",
      "&Agrave;",
      "&Aacute;",
      "&Acirc;",
      "&Atilde;",
      "&Auml;",
      "&Aring;",
      "&AElig;",
      "&Ccedil;",
      "&Egrave;",
      "&Eacute;",
      "&Ecirc;",
      "&Euml;",
      "&Igrave;",
      "&Iacute;",
      "&Icirc;",
      "&Iuml;",
      "&ETH;",
      "&Ntilde;",
      "&Ograve;",
      "&Oacute;",
      "&Ocirc;",
      "&Otilde;",
      "&Ouml;",
      "&times;",
      "&Oslash;",
      "&Ugrave;",
      "&Uacute;",
      "&Ucirc;",
      "&Uuml;",
      "&Yacute;",
      "&THORN;",
      "&alpha;",
      "&szlig;",
      "&agrave;",
      "&aacute;",
      "&acirc;",
      "&atilde;",
      "&auml;",
      "&aring;",
      "&aelig;",
      "&ccedil;",
      "&egrave;",
      "&eacute;",
      "&ecirc;",
      "&euml;",
      "&igrave;",
      "&iacute;",
      "&icirc;",
      "&iuml;",
      "&eth;",
      "&ntilde;",
      "&ograve;",
      "&oacute;",
      "&ocirc;",
      "&otilde;",
      "&ouml;",
      "&divide;",
      "&oslash;",
      "&ugrave;",
      "&uacute;",
      "&ucirc;",
      "&uuml;",
      "&yacute;",
      "&thorn;",
      "&yuml;",
      "&OElig;",
      "&oelig;",
      "&#372;",
      "&#374",
      "&#373",
      "&#375;",
      "&sbquo;",
      "&#8219;",
      "&bdquo;",
      "&hellip;",
      "&trade;",
      "&#9658;",
      "&bull;",
      "&rarr;",
      "&rArr;",
      "&hArr;",
      "&diams;",
      "&asymp;"
    ];
  }

  options(formControl: AbstractControl): any {
    const self = this;

    const onChange = editor => {
      editor.updateElement();

      if (formControl) {
        formControl.setValue(editor.getData());
        formControl.updateValueAndValidity();
        formControl.markAsTouched();
        formControl.markAsDirty();
      }
    };

    return {
      skin: "minimalist",
      language: "en",
      disableNativeSpellChecker: false,
      disableObjectResizing: true,
      extraPlugins: this.plugins().join(","),
      toolbar: [], // Empty array to hide default toolbar items but keep the toolbar plugin
      removePlugins: "floatingspace", // Only remove floating toolbar
      filebrowserUploadUrl: `${environment.classicBaseUrl}/json-api/common/ckeditor-upload/`,
      mentions: this.mentions(),
      smiley_columns: 10,
      smiley_path: "https://cdn.astrobin.com/static/astrobin/emoticons/",
      smiley_descriptions: this.smileyDescriptions(),
      smiley_images: this.smileyImages(),
      specialChars: this.specialChars(),
      fontSize_sizes: "XS/50%;S/75%;M/100%;L/150%;XL/200%",
      autoGrow_minHeight: 300,
      autoGrow_maxHeight: 1200,
      autoGrow_onStartup: true,
      autoGrow_bottomSpace: 50,
      codeSnippet_languages: {
        bash: "Bash",
        coffeescript: "CoffeeScript",
        cpp: "C++",
        cs: "C#",
        css: "CSS",
        diff: "Diff",
        html: "HTML",
        java: "Java",
        javascript: "JavaScript",
        json: "JSON",
        objectivec: "Objective-C",
        perl: "Perl",
        php: "PHP",
        python: "Python",
        ruby: "Ruby",
        sql: "SQL",
        vbscript: "VBScript",
        xhtml: "XHTML",
        xml: "XML"
      },
      on: {
        change() {
          onChange(this);
        },
        "simpleuploads.startUpload"() {
          self.loadingService.setLoading(true);
        },
        "simpleuploads.finishedUpload"() {
          onChange(this);
          self.loadingService.setLoading(false);
        },
        beforeCommandExec(event) {
          // Show the paste dialog for the paste buttons and right-click paste
          if (event.data.name === "paste") {
            event.editor._.forcePasteDialog = true;
          }

          // Don't show the paste dialog for Ctrl+Shift+V
          if (event.data.name === "pastetext" && event.data.commandData.from === "keystrokeHandler") {
            event.cancel();
          }
        }
      }
    };
  }

  /**
   * Load CKEditor and ensure it's ready for use
   * @returns Promise that resolves when CKEditor is ready
   */
  loadCKEditor(): Promise<void> {
    // If we already have a loading promise in progress, return it
    if (this._loadPromise !== null) {
      return this._loadPromise;
    }

    // If the editor is already ready, resolve immediately
    if (this._loadingState.value === CKEditorLoadingState.READY) {
      return Promise.resolve();
    }

    // If we're not in a browser environment, reject immediately
    if (!this._isBrowser) {
      this._loadingState.next(CKEditorLoadingState.ERROR);
      return Promise.reject("CKEditor not available in server-side rendering");
    }

    // Set loading state
    this._loadingState.next(CKEditorLoadingState.LOADING);

    // Create the loading promise
    this._loadPromise = new Promise<void>((resolve, reject) => {
      const win = this.windowRefService.nativeWindow as any;
      const document = this.windowRefService.nativeWindow.document;
      const timestamp = environment.ckeditorTimestamp;
      const baseUrl = `${environment.cdnUrl}/static/astrobin/ckeditor`;

      // Check if CKEditor is already fully loaded (including BBCode plugin)
      if (
        win.CKEDITOR &&
        typeof win.CKEDITOR.replace === "function" &&
        win.CKEDITOR.status === "loaded" &&
        this._bbCodePluginLoaded
      ) {
        // CKEditor and plugins are already loaded
        this._loadingState.next(CKEditorLoadingState.READY);
        resolve();
        return;
      }

      // Set the base path for CKEditor
      win.CKEDITOR_BASEPATH = `${baseUrl}/`;

      // Create and add script element for loading CKEditor
      const script = document.createElement("script");
      script.src = `${baseUrl}/ckeditor.js?t=${timestamp}`;

      // Set up completion check with timeout
      let timeoutId: any = null;

      // Function to check if CKEditor is ready and complete loading process
      const checkCKEditorReady = () => {
        if (this.isCKEditorReady(true)) {
          // CKEditor core is loaded, now load the plugins
          this._loadPlugins(baseUrl, timestamp)
            .then(() => {
              win.CKEDITOR.timestamp = timestamp;
              this._loadingState.next(CKEditorLoadingState.READY);
              if (timeoutId) {
                clearTimeout(timeoutId);
              }
              resolve();
            })
            .catch(error => {
              console.error("Error loading CKEditor plugins:", error);
              // Still mark as ready, we'll handle plugin errors separately
              this._loadingState.next(CKEditorLoadingState.READY);
              if (timeoutId) {
                clearTimeout(timeoutId);
              }
              resolve();
            });
          return true;
        }
        return false;
      };

      // Set up polling to check for CKEditor readiness
      const pollForReadiness = () => {
        if (!checkCKEditorReady()) {
          setTimeout(pollForReadiness, CKEDITOR_TIMEOUTS.CHECK_INTERVAL);
        }
      };

      // Set up final timeout
      timeoutId = setTimeout(() => {
        if (this._loadingState.value !== CKEditorLoadingState.READY) {
          // One final check before giving up
          if (checkCKEditorReady()) {
            return;
          }
          // If we reach here, CKEditor failed to load
          this._loadingState.next(CKEditorLoadingState.ERROR);
          reject("CKEditor failed to load properly after timeout");
        }
      }, CKEDITOR_TIMEOUTS.LOAD_TIMEOUT);

      // Script onload handler
      script.onload = () => {
        // Check if CKEDITOR object is available
        if (!win.CKEDITOR) {
          this._loadingState.next(CKEditorLoadingState.ERROR);
          reject("CKEditor failed to initialize properly");
          return;
        }

        // Set up event listener if available
        if (typeof win.CKEDITOR.on === "function") {
          win.CKEDITOR.on("loaded", () => {
            checkCKEditorReady();
          });
        }

        // Start polling for readiness
        pollForReadiness();
      };

      // Handle load error
      script.onerror = error => {
        console.error("Error loading CKEditor script:", error);
        this._loadingState.next(CKEditorLoadingState.ERROR);
        if (timeoutId) {
          clearTimeout(timeoutId);
        }
        reject("Failed to load CKEditor script");
      };

      // Add the script to the document
      if (document.readyState === "complete") {
        document.body.appendChild(script);
      } else {
        // Wait for document to be ready
        document.addEventListener("DOMContentLoaded", () => document.body.appendChild(script));
      }
    }).finally(() => {
      // Clear the promise reference when done, regardless of success or failure
      this._loadPromise = null;
    });

    return this._loadPromise;
  }

  /**
   * Load required CKEditor plugins
   * @param baseUrl Base URL for loading plugins
   * @param timestamp Cache-busting timestamp
   * @returns Promise that resolves when plugins are loaded
   */
  private _loadPlugins(baseUrl: string, timestamp: string): Promise<void> {
    return new Promise<void>(resolve => {
      try {
        const win = this.windowRefService.nativeWindow as any;
        const document = this.windowRefService.nativeWindow.document;

        // Verify that required dependencies are available
        const hasCore = win.CKEDITOR && typeof win.CKEDITOR.replace === "function";
        if (!hasCore) {
          console.warn("CKEditor core is not available, but continuing");
          resolve();
          return;
        }

        // Load BBCode plugin with timeout
        const bbcodeScript = document.createElement("script");
        bbcodeScript.src = `${baseUrl}/plugins/bbcode/plugin.js?t=${timestamp}`;
        let timeoutId: any = null;

        // Set timeout to resolve anyway if loading takes too long
        timeoutId = setTimeout(() => {
          console.warn("BBCode plugin loading timed out, but continuing with CKEditor");
          resolve();
        }, CKEDITOR_TIMEOUTS.LOAD_TIMEOUT);

        bbcodeScript.onload = () => {
          // Successfully loaded BBCode plugin
          clearTimeout(timeoutId);
          this._bbCodePluginLoaded = true;
          resolve();
        };

        bbcodeScript.onerror = () => {
          // BBCode plugin failed to load, but we can still use the editor
          clearTimeout(timeoutId);
          console.warn("BBCode plugin failed to load, but continuing with CKEditor");
          resolve();
        };

        document.body.appendChild(bbcodeScript);
      } catch (error) {
        // Error during plugin loading, but we can still try to use the editor
        console.error("Error during plugin loading:", error);
        resolve();
      }
    });
  }

  private _fetchThumbnail(src: string, callback: (thumbnailSrc: string) => void) {
    const urlPath = new URL(src, this.windowRefService.nativeWindow.location.origin).pathname.slice(1);
    const url = `${environment.classicBaseUrl}/json-api/common/ckeditor-upload/?path=${encodeURIComponent(urlPath)}`;

    this.http
      .get<{ thumbnail: string }>(url)
      .pipe(
        map(response => response.thumbnail),
        catchError(error => {
          console.error("Error fetching thumbnail:", error);
          return of(src);
        })
      )
      .subscribe(thumbnail => {
        if (thumbnail) {
          callback(thumbnail);
        } else {
          callback(src);
        }
      });
  }
}
